import React from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import { getJustificationOptions } from 'services/justification';
import { postPauseActivity } from 'services/activity';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import dayjs from 'dayjs';
import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import { useFormik } from 'formik';
import * as yup from 'yup';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import BaseModal from '../BaseModal';
import { IModalPauseProps } from './types';
import useStyles from './style';

const FORM_ID = 'PAUSE_FORM_MODAL';

const MAX_DATE = dayjs().format('YYYY-MM-DD');
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';

type IOption = {
  value: string | number;
  label: string | number;
};

const ModalPause = ({
  children,
  open,
  onClose,
  gridRow,
  reloadData,
}: IModalPauseProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [justificationOptions, setJustificationOptions] = React.useState<
    IOption[]
  >([]);

  const validationSchema = yup.object({
    justification: yup.string().required('Obrigatório'),
    pauseStart: yup
      .mixed()
      .required(DEFAULT_HELPER_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const minDate = gridRow?.realStart;
        const test = dayjs(value);
        return (
          dayjs(value).isValid() &&
          (test.isSame(minDate) ||
            (test.isAfter(minDate) && test.isBefore(dayjs())))
        );
      })
      .typeError(DEFAULT_ERROR_TEXT),
  });

  const queryJustificationOptions = useQuery({
    queryKey: ['JustificationOptionsContext'],
    queryFn: async () => {
      const { data } = await getJustificationOptions();
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setJustificationOptions(data);
    },
    enabled: true,
  });

  const formik = useFormik({
    initialValues: {
      equipment: '',
      activity: '',
      realStart: null,
      pauseStart: null,
      justification: '',
      comment: '',
    },
    validationSchema,
    onSubmit: formValues => {
      const { pauseStart, justification, comment } = formValues;

      const params = {
        activityId: gridRow?.activityRealId,
        pauseDate: dayjs(pauseStart).utc().format('YYYY-MM-DDTHH:mm:ssZ'),
        justificationId: justification,
        observation: comment,
      };

      // se for enviado com sucesso
      setLoadingSaveForm(true);
      postPauseActivity(params)
        .then(() => {
          // solicita um reload de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldValue(name, value);
    },
    [formik],
  );
  React.useEffect(() => {
    // se existir alteracoes no campo & estiver com a modal fechada
    if (formik.dirty && !open) {
      // então: reset form quando eu fecho o modal,
      formik.resetForm();
    }
  }, [formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          <Grid item xs={12}>
            <Typography className={classes.formMessage}>
              Prencha os dados obrigatórios para pausar a atividade:
            </Typography>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="equipment"
                name="equipment"
                label="Equipamento"
                variant="outlined"
                disabled
                value={gridRow?.equipment}
                onChange={formik.handleChange}
                error={
                  formik.touched.equipment && Boolean(formik.errors.equipment)
                }
                helperText={formik.touched.equipment && formik.errors.equipment}
              />
            </Grid>
          </Grid>

          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="activity"
                name="activity"
                variant="outlined"
                label="Atividade"
                disabled
                value={gridRow?.activity}
                onChange={formik.handleChange}
                error={
                  formik.touched.activity && Boolean(formik.errors.activity)
                }
                helperText={formik.touched.activity && formik.errors.activity}
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                id="realStart"
                name="realStart"
                label="Início real"
                inputVariant="outlined"
                disabled
                format="DD/MM"
                keyboardIcon={null}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                value={gridRow?.realStart}
                onChange={date => handleDateChange('realStart', date)}
              />
            </Grid>
          </Grid>

          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6}>
              <KeyboardDatePicker
                id="pauseStart"
                name="pauseStart"
                label="Início da pausa"
                inputVariant="outlined"
                format="DD/MM"
                placeholder="dd/mm"
                clearable
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                minDate={gridRow?.realStart}
                maxDate={MAX_DATE}
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                value={formik.values.pauseStart}
                onChange={date => handleDateChange('pauseStart', date)}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.pauseStart && Boolean(formik.errors.pauseStart)
                }
                helperText={formik.errors.pauseStart || DEFAULT_HELPER_TEXT}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                id="justification"
                name="justification"
                label="Justificativa"
                variant="outlined"
                value={formik.values.justification}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.justification &&
                  Boolean(formik.errors.justification)
                }
                helperText={formik.errors.justification || 'Obrigatório'}
                InputProps={{
                  endAdornment: queryJustificationOptions?.isLoading && (
                    <InputAdornment position="start">
                      <CircularProgress color="inherit" size={25} />
                    </InputAdornment>
                  ),
                }}
              >
                {justificationOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="comment"
                name="comment"
                label="Comentário"
                variant="outlined"
                value={formik.values.comment}
                onChange={formik.handleChange}
                error={formik.touched.comment && Boolean(formik.errors.comment)}
                helperText="Opcional"
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }, [
    formik.handleSubmit,
    formik.handleChange,
    formik.touched.equipment,
    formik.touched.activity,
    formik.touched.pauseStart,
    formik.touched.justification,
    formik.touched.comment,
    formik.errors.equipment,
    formik.errors.activity,
    formik.errors.pauseStart,
    formik.errors.justification,
    formik.errors.comment,
    formik.values.pauseStart,
    formik.values.justification,
    formik.values.comment,
    formik.handleBlur,
    classes.bodyContent,
    classes.formMessage,
    gridRow?.equipment,
    gridRow?.activity,
    gridRow?.realStart,
    queryJustificationOptions?.isLoading,
    justificationOptions,
    handleDateChange,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);
  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <BaseModal
          title="Pausar"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="629px"
        >
          {children}
        </BaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};
export default ModalPause;
